import { mainnet, sepolia, polygon, polygonAmoy, arbitrum } from 'viem/chains'
import type { Chain } from 'viem'

/**
 * Registry of EVM chains the app knows how to talk to.
 * Adding a new chain = import it from `viem/chains` and append it here.
 *
 * The active chain is selected at runtime by `VITE_CHAIN_ID`
 * (with `RPC_URL` providing the endpoint).
 */
export const SUPPORTED_CHAINS = [
  sepolia,
  mainnet,
  polygon,
  polygonAmoy,
  arbitrum,
] as const satisfies readonly Chain[]

/** Default chain when env is missing — Sepolia keeps legacy behaviour. */
export const DEFAULT_CHAIN: Chain = sepolia

/** Returns the viem `Chain` for a given chain id, or `undefined` if unknown. */
export function findChainById(chainId: number): Chain | undefined {
  return SUPPORTED_CHAINS.find((c) => c.id === chainId)
}

/**
 * Resolve a chain id to a viem `Chain`. Falls back to {@link DEFAULT_CHAIN}
 * (with a console warning) when the id is not in {@link SUPPORTED_CHAINS}
 * so the app still boots in misconfigured environments.
 */
export function getChainOrDefault(chainId: number): Chain {
  const chain = findChainById(chainId)
  if (chain) return chain
  if (typeof console !== 'undefined') {
    console.warn(
      `[chains] Unknown chain id ${chainId}, falling back to ${DEFAULT_CHAIN.name} (${DEFAULT_CHAIN.id}). ` +
        `Add the chain to src/config/chains.ts to fix.`,
    )
  }
  return DEFAULT_CHAIN
}

/**
 * Stable short identifier used in logs / IPFS payload `chain` field.
 * Returns the lowercase `viem` chain key (e.g. `sepolia`, `polygonAmoy`).
 */
export function getChainSlug(chainId: number): string {
  return findChainById(chainId)?.name.replace(/\s+/g, '') || `chain-${chainId}`
}

/** Default block-explorer URL (https) for a chain id, or empty string. */
export function getExplorerUrl(chainId: number): string {
  const chain = findChainById(chainId)
  return chain?.blockExplorers?.default?.url ?? ''
}

/** Default public RPC URL shipped by viem for a chain id, or empty string. */
export function getDefaultRpcUrl(chainId: number): string {
  const chain = findChainById(chainId)
  return chain?.rpcUrls?.default?.http?.[0] ?? ''
}
